import { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Menubar } from 'primereact/menubar';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Avatar } from 'primereact/avatar';
import { Badge } from 'primereact/badge';
import { ToastContainer } from './Toast.jsx';
import YAML from 'js-yaml';
import fileDownload from 'js-file-download';

import 'primereact/resources/themes/lara-light-indigo/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';

const PrimeMenu = (props) => {
  const { getComponent, backendSpecsActions, backendSpecsSelectors, pagoAuth2Selectors, pagoAuth2Actions, userRole, editorSelectors } = props;
  const [isSaveDialogOpen, setIsSaveDialogOpen] = useState(false);
  const [isLoadDialogOpen, setIsLoadDialogOpen] = useState(false);
  const [isSpecsManagerOpen, setIsSpecsManagerOpen] = useState(false);
  const [isHealthStatusOpen, setIsHealthStatusOpen] = useState(false);
  const [isSpecAccessOpen, setIsSpecAccessOpen] = useState(false);
  const [isShareLinksOpen, setIsShareLinksOpen] = useState(false);
  const [isUserDialogOpen, setIsUserDialogOpen] = useState(false);
  const [toasts, setToasts] = useState([]);
  const toastIdRef = useRef(0);

  const SaveSpecDialog = getComponent('SaveSpecDialog', true);
  const LoadFromBackendDialog = getComponent('LoadFromBackendDialog', true);
  const SpecsManagerDialog = getComponent('SpecsManagerDialog', true);
  const HealthStatusDialog = getComponent('HealthStatusDialog', true);
  const SpecAccessDialog = getComponent('SpecAccessDialog', true);
  const ShareLinksDialog = getComponent('ShareLinksDialog', true);

  const isAdmin = userRole === 'MOCKORCHESTRATOR_ADMIN';
  const isEditorOrAdmin = userRole === 'MOCKORCHESTRATOR_EDITOR' || isAdmin;

  const addToast = useCallback((message, type = 'success', duration = 4000) => {
    const id = toastIdRef.current++;
    setToasts((prev) => [...prev, { id, message, type, duration }]);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  // Check if user has restricted role
  const isViewerOrViewerEnv = userRole === 'MOCKORCHESTRATOR_VIEWER' || userRole === 'MOCKORCHESTRATOR_VIEWER_ENV';

  const getCurrentSpec = useCallback(() => {
    const reduxSpec = backendSpecsSelectors?.selectCurrentSpec?.();
    if (reduxSpec?.id) {
      return { id: reduxSpec.id, name: reduxSpec.name };
    }
    return null;
  }, [backendSpecsSelectors]);

  // Direct Save handler (Ctrl+S)
  const handleDirectSave = useCallback(async () => {
    const spec = getCurrentSpec();

    if (!spec?.id) {
      setIsSaveDialogOpen(true);
    } else {
      if (backendSpecsActions?.saveSpec) {
        const result = await backendSpecsActions.saveSpec(spec.name, spec.id);
        if (result?.error) {
          addToast(result.meta?.errorMessage || 'Failed to save spec', 'error');
        } else {
          addToast(`Spec "${spec.name}" saved successfully`, 'success');
        }
      }
    }
  }, [backendSpecsActions, addToast, getCurrentSpec]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Ctrl+S or Cmd+S for save
      if ((event.ctrlKey || event.metaKey) && event.key === 's') {
        event.preventDefault();
        if (!isViewerOrViewerEnv) {
          handleDirectSave();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleDirectSave, isViewerOrViewerEnv]);

  // Menu item handlers
  const handleSaveClick = useCallback(() => {
    setIsSaveDialogOpen(true);
  }, []);

  const handleLoadFromServerClick = useCallback(() => {
    setIsLoadDialogOpen(true);
  }, []);

  const handleSpecsManagerClick = useCallback(() => {
    setIsSpecsManagerOpen(true);
  }, []);

  const handleHealthStatusClick = useCallback(() => {
    setIsHealthStatusOpen(true);
  }, []);

  const handleSpecAccessClick = useCallback(() => {
    setIsSpecAccessOpen(true);
  }, []);

  const handleShareLinksClick = useCallback(() => {
    setIsShareLinksOpen(true);
  }, []);

  const handleUserDialogOpen = useCallback(() => {
    setIsUserDialogOpen(true);
  }, []);

  const handleUserDialogClose = useCallback(() => {
    setIsUserDialogOpen(false);
  }, []);

  const handleRefreshUser = useCallback(async () => {
    console.log('[PrimeMenu] handleRefreshUser called');
    console.log('[PrimeMenu] pagoAuth2Actions:', pagoAuth2Actions);
    
    if (pagoAuth2Actions?.fetchCurrentUser) {
      addToast('Fetching user info...', 'info');
      try {
        const result = await pagoAuth2Actions.fetchCurrentUser();
        console.log('[PrimeMenu] fetchCurrentUser result:', result);
        if (result?.error) {
          addToast(`Failed: ${result.meta?.errorMessage || 'Unknown error'}`, 'error');
        } else {
          addToast('User info refreshed!', 'success');
        }
      } catch (error) {
        console.error('[PrimeMenu] fetchCurrentUser error:', error);
        addToast(`Error: ${error.message}`, 'error');
      }
    } else {
      console.error('[PrimeMenu] pagoAuth2Actions.fetchCurrentUser not available');
      addToast('pagoAuth2Actions not available', 'error');
    }
  }, [pagoAuth2Actions, addToast]);

  const handleNewSpecClick = useCallback(() => {
    if (backendSpecsActions?.newSpec) {
      backendSpecsActions.newSpec();
      addToast('New spec created', 'success');
    }
  }, [backendSpecsActions, addToast]);

  // Dialog close handlers
  const handleSaveDialogClose = useCallback(() => {
    setIsSaveDialogOpen(false);
  }, []);

  const handleSaveDialogSave = useCallback(
    async (specName) => {
      if (backendSpecsActions?.saveSpec) {
        const existingSpec = getCurrentSpec();
        const result = await backendSpecsActions.saveSpec(specName, existingSpec?.id);
        if (result?.error) {
          addToast(result.meta?.errorMessage || 'Failed to save spec', 'error');
        } else if (result?.payload) {
          addToast(`Spec "${specName}" saved successfully`, 'success');
        }
      }
      setIsSaveDialogOpen(false);
    },
    [backendSpecsActions, addToast, getCurrentSpec]
  );

  const handleLoadDialogClose = useCallback(() => {
    setIsLoadDialogOpen(false);
  }, []);

  const handleLoadSelect = useCallback(
    async (specId, selectedSpec) => {
      if (backendSpecsActions?.loadSpecIntoEditor && specId) {
        const result = await backendSpecsActions.loadSpecIntoEditor(specId);
        if (result?.error) {
          addToast(result.meta?.errorMessage || 'Failed to load spec', 'error');
        } else {
          addToast(`Spec "${selectedSpec?.name || specId}" loaded successfully`, 'success');
        }
      }
      setIsLoadDialogOpen(false);
    },
    [backendSpecsActions, addToast]
  );

  const handleSpecsManagerClose = useCallback(() => {
    setIsSpecsManagerOpen(false);
  }, []);

  const handleHealthStatusClose = useCallback(() => {
    setIsHealthStatusOpen(false);
  }, []);

  const handleSpecAccessClose = useCallback(() => {
    setIsSpecAccessOpen(false);
  }, []);

  const handleShareLinksClose = useCallback(() => {
    setIsShareLinksOpen(false);
  }, []);

  // Navigate to Figma Annotator
  const handleFigmaClick = useCallback(() => {
    window.location.href = '/figma-annotator';
  }, []);

  // Navigate to Figma Annotator POC
  const handleFigmaPocClick = useCallback(() => {
    window.location.href = '/figma-annotator-poc';
  }, []);

  // Download JSON - converts YAML to JSON and downloads
  const handleDownloadJson = useCallback(() => {
    try {
      const currentSpec = getCurrentSpec();
      const specName = currentSpec?.name || 'spec';
      // Get content from editor via editorSelectors
      const editorContent = editorSelectors?.selectContent?.() || '';
      
      if (!editorContent) {
        addToast('No content to download', 'error');
        return;
      }

      // Parse YAML and convert to JSON
      const contentObject = YAML.load(editorContent);
      const jsonContent = JSON.stringify(contentObject, null, 2);
      
      // Download file
      fileDownload(jsonContent, `${specName}.json`);
      addToast('JSON downloaded successfully', 'success');
    } catch (error) {
      addToast(`Failed to convert to JSON: ${error.message}`, 'error');
    }
  }, [getCurrentSpec, editorSelectors, addToast]);

  // Download YAML - downloads current content as YAML file
  const handleDownloadYaml = useCallback(() => {
    try {
      const currentSpec = getCurrentSpec();
      const specName = currentSpec?.name || 'spec';
      const editorContent = editorSelectors?.selectContent?.() || '';
      
      if (!editorContent) {
        addToast('No content to download', 'error');
        return;
      }

      fileDownload(editorContent, `${specName}.yaml`);
      addToast('YAML downloaded successfully', 'success');
    } catch (error) {
      addToast(`Failed to download YAML: ${error.message}`, 'error');
    }
  }, [getCurrentSpec, editorSelectors, addToast]);

  // Build menu items
  const getMenuItems = () => {
    const items = [];

    // File Menu
    const fileMenuItems = [];

    // Quick Save (for existing specs) or Save As (for new specs)
    if (!isViewerOrViewerEnv) {
      const currentSpec = getCurrentSpec();
      if (currentSpec?.id) {
        // Quick save for existing specs
        fileMenuItems.push({
          label: 'Save',
          icon: 'pi pi-save',
          command: handleDirectSave,
          template: (item, options) => (
            <a
              className={options.className}
              onClick={options.onClick}
              style={{
                background: '#22c55e',
                color: 'white',
                fontWeight: 'bold',
                borderRadius: '4px',
                margin: '2px 4px',
              }}
            >
              <span className="pi pi-save mr-2"></span>
              <span>Save (Ctrl+S)</span>
            </a>
          ),
        });
      } else {
        // Save As for new specs
        fileMenuItems.push({
          label: 'Save As...',
          icon: 'pi pi-save',
          command: handleSaveClick,
          template: (item, options) => (
            <a
              className={options.className}
              onClick={options.onClick}
              style={{
                background: '#22c55e',
                color: 'white',
                fontWeight: 'bold',
                borderRadius: '4px',
                margin: '2px 4px',
              }}
            >
              <span className="pi pi-save mr-2"></span>
              <span>Save As...</span>
            </a>
          ),
        });
      }

      fileMenuItems.push({
        label: 'New',
        icon: 'pi pi-file',
        command: handleNewSpecClick,
      });
    } else {
      // Viewer mode - only New and Save As
      fileMenuItems.push(
        {
          label: 'New',
          icon: 'pi pi-file',
          command: handleNewSpecClick,
        },
        {
          label: 'Save As...',
          icon: 'pi pi-save',
          command: handleSaveClick,
        }
      );
    }

    if (!isViewerOrViewerEnv) {
      fileMenuItems.push(
        {
          separator: true,
        },
        {
          label: 'Load from Server',
          icon: 'pi pi-cloud-download',
          command: handleLoadFromServerClick,
        },
        {
          separator: true,
        },
        {
          label: 'Manage Specs',
          icon: 'pi pi-list',
          command: handleSpecsManagerClick,
        }
      );

      // Add Spec Access for admins in File menu too
      if (isAdmin) {
        fileMenuItems.push({
          label: 'User Access',
          icon: 'pi pi-users',
          command: handleSpecAccessClick,
        });
      }
    }

    items.push({
      label: 'File',
      icon: 'pi pi-folder',
      items: fileMenuItems,
    });

    // Tools Menu (for non-viewer users)
    if (!isViewerOrViewerEnv) {
      const toolsItems = [
        {
          label: 'Health Status',
          icon: 'pi pi-heart',
          command: handleHealthStatusClick,
        },
        {
          label: 'Download JSON',
          icon: 'pi pi-file-export',
          command: handleDownloadJson,
        },
        {
          label: 'Download YAML',
          icon: 'pi pi-file-export',
          command: handleDownloadYaml,
        },
      ];

      if (isEditorOrAdmin) {
        toolsItems.push(
          { separator: true },
          {
            label: 'Share Links',
            icon: 'pi pi-link',
            command: handleShareLinksClick,
          }
        );
      }

      if (isAdmin) {
        toolsItems.push(
          { separator: true },
          {
            label: 'User Access',
            icon: 'pi pi-users',
            command: handleSpecAccessClick,
          }
        );
      }

      items.push({
        label: 'Tools',
        icon: 'pi pi-cog',
        items: toolsItems,
      });

      // Figma Menu (top level)
      items.push({
        label: 'Figma',
        icon: 'pi pi-palette',
        items: [
          {
            label: 'Figma Annotator',
            icon: 'pi pi-image',
            command: handleFigmaClick,
          },
          {
            label: 'Figma Annotator (POC)',
            icon: 'pi pi-image',
            command: handleFigmaPocClick,
          },
        ],
      });

      // Generate Menu
      items.push({
        label: 'Generate',
        icon: 'pi pi-code',
        items: [
          {
            label: 'Server',
            icon: 'pi pi-server',
            items: [
              { label: 'OpenAPI 3.x', command: () => { /* Trigger generate */ } },
              { label: 'OpenAPI 2.0', command: () => { /* Trigger generate */ } },
            ],
          },
          {
            label: 'Client',
            icon: 'pi pi-desktop',
            items: [
              { label: 'OpenAPI 3.x', command: () => { /* Trigger generate */ } },
              { label: 'OpenAPI 2.0', command: () => { /* Trigger generate */ } },
            ],
          },
        ],
      });
    }

    return items;
  };

  const menuItems = getMenuItems();
  const currentSpec = getCurrentSpec();
  const isNewSpec = !currentSpec?.id;

  return (
    <>
      {/* Dialogs */}
      {SaveSpecDialog && (
        <SaveSpecDialog
          isOpen={isSaveDialogOpen}
          onClose={handleSaveDialogClose}
          onSave={handleSaveDialogSave}
          getComponent={getComponent}
          currentSpecName={currentSpec?.name || ''}
          isNewSpec={isNewSpec}
        />
      )}
      {LoadFromBackendDialog && (
        <LoadFromBackendDialog
          isOpen={isLoadDialogOpen}
          onClose={handleLoadDialogClose}
          onSelect={handleLoadSelect}
          getComponent={getComponent}
          backendSpecsActions={backendSpecsActions}
          backendSpecsSelectors={backendSpecsSelectors}
        />
      )}
      {SpecsManagerDialog && (
        <SpecsManagerDialog
          isOpen={isSpecsManagerOpen}
          onClose={handleSpecsManagerClose}
          getComponent={getComponent}
          backendSpecsActions={backendSpecsActions}
          backendSpecsSelectors={backendSpecsSelectors}
        />
      )}
      {HealthStatusDialog && (
        <HealthStatusDialog
          isOpen={isHealthStatusOpen}
          onClose={handleHealthStatusClose}
          getComponent={getComponent}
          backendSpecsActions={backendSpecsActions}
          backendSpecsSelectors={backendSpecsSelectors}
        />
      )}
      {isAdmin && SpecAccessDialog && (
        <SpecAccessDialog
          isOpen={isSpecAccessOpen}
          onClose={handleSpecAccessClose}
          getComponent={getComponent}
          backendSpecsActions={backendSpecsActions}
          backendSpecsSelectors={backendSpecsSelectors}
        />
      )}
      {isEditorOrAdmin && ShareLinksDialog && (
        <ShareLinksDialog
          isOpen={isShareLinksOpen}
          onClose={handleShareLinksClose}
          getComponent={getComponent}
          backendSpecsActions={backendSpecsActions}
          backendSpecsSelectors={backendSpecsSelectors}
        />
      )}

      <ToastContainer toasts={toasts} removeToast={removeToast} />

      {/* User Details Dialog */}
      <Dialog
        header="User Details"
        visible={isUserDialogOpen}
        onHide={handleUserDialogClose}
        style={{ width: '450px' }}
        breakpoints={{ '960px': '75vw', '641px': '100vw' }}
      >
        <div className="user-details-content">
          <div className="user-avatar-section" style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
            <Avatar icon="pi pi-user" size="xlarge" shape="circle" style={{ background: '#173647', color: 'white' }} />
          </div>
          
          <div className="user-info-section">
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ fontWeight: 'bold', color: '#666', display: 'block', marginBottom: '0.25rem' }}>
                Username:
              </label>
              <div style={{ fontSize: '1.1rem', padding: '0.5rem', background: '#f5f5f5', borderRadius: '4px' }}>
                {(() => {
                  const user = pagoAuth2Selectors?.selectCurrentUser?.();
                  if (typeof user === 'string') return user;
                  return user?.username || user?.sub || user?.preferred_username || JSON.stringify(user) || 'Unknown';
                })()}
              </div>
            </div>
            
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ fontWeight: 'bold', color: '#666', display: 'block', marginBottom: '0.25rem' }}>
                Current Role (from prop):
              </label>
              <div style={{ fontSize: '1.1rem', padding: '0.5rem', background: '#f5f5f5', borderRadius: '4px', fontFamily: 'monospace' }}>
                {userRole || 'Not loaded'}
                {userRole === 'MOCKORCHESTRATOR_ADMIN' && (
                  <Badge value="ADMIN" severity="success" style={{ marginLeft: '0.5rem' }} />
                )}
              </div>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ fontWeight: 'bold', color: '#666', display: 'block', marginBottom: '0.25rem' }}>
                Current Role (from selector):
              </label>
              <div style={{ fontSize: '1.1rem', padding: '0.5rem', background: '#e3f2fd', borderRadius: '4px', fontFamily: 'monospace' }}>
                {pagoAuth2Selectors?.selectUserRole?.() || 'Not loaded'}
              </div>
            </div>
            
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ fontWeight: 'bold', color: '#666', display: 'block', marginBottom: '0.25rem' }}>
                Role Checks:
              </label>
              <div style={{ fontSize: '0.9rem', padding: '0.5rem', background: '#f9f9f9', borderRadius: '4px' }}>
                <div>isAdmin: {pagoAuth2Selectors?.selectIsAdmin?.() ? '✅ YES' : '❌ NO'}</div>
                <div>isEditor: {pagoAuth2Selectors?.selectIsEditor?.() ? '✅ YES' : '❌ NO'}</div>
                <div>isViewer: {pagoAuth2Selectors?.selectIsViewer?.() ? '✅ YES' : '❌ NO'}</div>
                <div>isViewerEnv: {pagoAuth2Selectors?.selectIsViewerEnv?.() ? '✅ YES' : '❌ NO'}</div>
                <div>isViewerOrViewerEnv: {pagoAuth2Selectors?.selectIsViewerOrViewerEnv?.() ? '✅ YES' : '❌ NO'}</div>
              </div>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ fontWeight: 'bold', color: '#666', display: 'block', marginBottom: '0.25rem' }}>
                User Environment:
              </label>
              <div style={{ fontSize: '0.9rem', padding: '0.5rem', background: '#f9f9f9', borderRadius: '4px' }}>
                {(() => {
                  const env = pagoAuth2Selectors?.selectUserEnvironment?.();
                  return env ? `${env.name} (ID: ${env.id})` : 'None';
                })()}
              </div>
            </div>

            <div>
              <label style={{ fontWeight: 'bold', color: '#666', display: 'block', marginBottom: '0.25rem' }}>
                Allowed Spec IDs:
              </label>
              <div style={{ fontSize: '0.9rem', padding: '0.5rem', background: '#f9f9f9', borderRadius: '4px', maxHeight: '100px', overflow: 'auto' }}>
                {(() => {
                  const ids = pagoAuth2Selectors?.selectAllowedSpecIds?.();
                  return ids?.length > 0 ? ids.join(', ') : 'All specs allowed';
                })()}
              </div>
            </div>

            <div style={{ marginTop: '1rem', padding: '0.5rem', background: '#fff3e0', borderRadius: '4px', fontSize: '0.85rem' }}>
              <strong>Debug Info:</strong><br/>
              isAdmin check: {isAdmin ? 'TRUE (will show User Access)' : 'FALSE (no User Access)'}
            </div>

            <div style={{ marginTop: '1rem', textAlign: 'center' }}>
              <Button
                label="Refresh User Info"
                icon="pi pi-refresh"
                onClick={handleRefreshUser}
                severity="info"
                size="small"
              />
            </div>
          </div>
        </div>
      </Dialog>

      {/* PrimeReact Menubar with user icon */}
      <div className="pago-prime-menu">
        <Menubar 
          model={menuItems} 
          end={
            <Button
              icon="pi pi-user"
              rounded
              text
              severity="info"
              aria-label="User"
              onClick={handleUserDialogOpen}
              style={{ color: 'white' }}
              tooltip="View user details"
              tooltipOptions={{ position: 'bottom' }}
            />
          }
        />
      </div>
    </>
  );
};

PrimeMenu.propTypes = {
  getComponent: PropTypes.func.isRequired,
  backendSpecsActions: PropTypes.object,
  backendSpecsSelectors: PropTypes.object,
  pagoAuth2Selectors: PropTypes.object,
  pagoAuth2Actions: PropTypes.object,
  userRole: PropTypes.string,
  editorSelectors: PropTypes.shape({
    selectContent: PropTypes.func,
  }),
};

PrimeMenu.defaultProps = {
  backendSpecsActions: null,
  backendSpecsSelectors: null,
  pagoAuth2Selectors: null,
  pagoAuth2Actions: null,
  userRole: null,
  editorSelectors: null,
};

export default PrimeMenu;










